import purchaseHistory from "../purchaseHistory"

// Express (in stock) flag per product id, kept for the rest of the page view so
// the next keystroke's results don't ask stock-and-price again for ids already
// looked up. It holds the promise, so an id that is still being fetched isn't
// requested a second time either.
const expressStockById = new Map()

const lookupExpressStock = (ids) => {
    const missing = ids.filter((id) => !expressStockById.has(id))

    if (missing.length > 0) {
        const request = window.api.getPricesAndStock(missing.map((id) => ({ id, quantity: 1 })))

        missing.forEach((id) => {
            const inStock = request.then((results) => results[id]?.data?.quantity > 0)

            // forget a failed lookup, so a later search can try that id again
            inStock.catch(() => {
                if (expressStockById.get(id) === inStock) {
                    expressStockById.delete(id)
                }
            })

            expressStockById.set(id, inStock)
        })
    }

    return Promise.all(ids.map((id) => expressStockById.get(id)))
}

const mainSearch = (data) => ({
    query: '',
    submitUrl: data.submitUrl,
    currentIso: window.accuvars.language.iso_code,
    showingProducts: true,
    showingCategories: false,
    showingCms: false,
    productResults: [],
    categoryResults: [],
    cmsResults: [],
    productHitCount: 0,
    categoryHitCount: 0,
    cmsHitCount: 0,
    showSearchResults: false,
    loading: false,
    productsToRetrieve: 5,
    categoriesToRetrieve: 5,
    cmsToRetrieve: 5,
    productQueryId: null,
    cmsQueryId: null,
    categoryQueryId: null,
    lastHits: 0,
    init() {
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.get('query') != null) {
            this.query = urlParams.get('query')
        }
        this.searchHistory = window.Alpine.store('search').history
    },
    resetStats() {
        this.productHitCount = 0
        this.categoryHitCount = 0
        this.cmsHitCount = 0
        this.productResults = []
        this.cmsResults = []
        this.categoryResults = []
    },
    hideSearch() {
        if (!this.showSearchResults) {
            return
        }
        this.showSearchResults = false
        window.Alpine.store('general').showOverlay = false
    },
    decideToShow(event) {
        this.showSearchResults = true
        window.Alpine.store('general').showOverlay = this.showSearchResults

        if (this.query.trim() !== '') {
            this.performSearch(event)
        }
    },
    performSearch(event) {
        this.loading = true

        if (this.query.trim() === '') {
            this.loading = false
            this.lastHits = 0
            this.resetStats()
            return true
        }

        if (
            event.type !== 'click' && event.which === 13
        ) {
            // submit form on ENTER press
            this.loading = false
            return true
        }

        if (
            event.type !== 'click'
            && String.fromCharCode(event.keyCode).match(/(\w|\s|\W)/g) === null
            && event.which !== 8
            && event.which !== 32
        ) {
            // non char key, dont trigger search
            // but accept backspace or space or click since it changes string
            this.loading = false
            return true
        }

        this.pushQueryToHistory()
        this.showSearchResults = true

        // build query array, for cms just one entry

        const queries = [
            {
                indexName: 'new_cms_pages',
                query: this.query,
                restrictSearchableAttributes: [
                    `${this.currentIso}.title`,
                    `${this.currentIso}.meta_title`,
                    `${this.currentIso}.meta_keywords`,
                    `${this.currentIso}.meta_description`,
                ],
                params: {
                    hitsPerPage: this.cmsToRetrieve,
                },
                clickAnalytics: true,
            }, {
                indexName: 'new_products',
                query: this.query,
                restrictSearchableAttributes: [
                    'manufacturer_name',
                    'reference',
                    `${this.currentIso}.title`,
                    `${this.currentIso}.meta_keywords`,
                    `${this.currentIso}.meta_description`,
                    `${this.currentIso}.category_name`,
                    `${this.currentIso}.attributes`,
                ],
                params: {
                    hitsPerPage: this.productsToRetrieve,
                },
                clickAnalytics: true,
            }, {
                indexName: 'new_product_categories',
                query: this.query,
                restrictSearchableAttributes: [
                    'product_prefixes',
                    `${this.currentIso}.title`,
                    `${this.currentIso}.meta_title`,
                    `${this.currentIso}.meta_keywords`,
                    `${this.currentIso}.meta_description`,
                ],
                params: {
                    hitsPerPage: this.categoriesToRetrieve,
                },
                clickAnalytics: true,
            },
        ]

        window.api.search(queries).then((searchResults) => {
            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    event: 'gtm_algolia_search',
                    payload: {
                        search_query: this.query,
                        page_url: window.location.href,
                    },
                },
            }))

            // no indexes means some went wrong , but handle with reset
            if (searchResults.length === 0) {
                this.resetStats()
            } else {
                // 1st resultset is CMS
                const cmsResults = searchResults[0]
                // 2nd resultset are products (+category extras for now)
                const productResults = searchResults[1]
                // 3rd category results
                const categoryResults = searchResults[2]

                this.cmsResults = cmsResults.hits
                this.cmsHitCount = cmsResults.nbHits
                this.cmsQueryId = cmsResults.queryID
                this.productResults = productResults.hits
                this.productHitCount = productResults.nbHits
                this.productQueryId = productResults.queryID
                this.categoryResults = categoryResults.hits
                this.categoryHitCount = categoryResults.nbHits
                this.categoryQueryId = categoryResults.queryID

                this.lastHits = this.categoryHitCount + this.productHitCount + this.cmsHitCount

                const rows = this.productResults
                const ids = [...new Set(rows.map((row) => String(row.objectID)))]

                this.productResults.forEach((resultItem) => {
                    purchaseHistory.getProductHistory(resultItem.objectID).then(data => resultItem.productHistory = data)
                })

                // add Express badges to products, matched by id. A newer search
                // may have replaced these results by the time stock comes back,
                // so a reply for a superseded list is dropped.
                if (ids.length > 0) {
                    lookupExpressStock(ids).then((inStock) => {
                        if (this.productResults !== rows) {
                            return
                        }

                        const inStockById = new Map(ids.map((id, i) => [id, inStock[i]]))

                        for (let x = 0; x < rows.length; x += 1) {
                            rows[x].isExpress = inStockById.get(String(rows[x].objectID))
                        }
                    }).catch(() => {
                        // the badge is optional: leave it unset if stock can't be read
                    })
                }

                // impressions from search
                const impressionProducts = []
                const impressionProductsGA4 = []

                for (let x = 0; x < this.productResults.length; x += 1) {
                    const impressionProduct = this.productResults[x]

                    impressionProducts.push({
                        name: impressionProduct[this.currentIso].title,
                        id: impressionProduct.reference,
                        category: impressionProduct[this.currentIso].category_name,
                        list: 'Main Search',
                        position: x + 1,
                    })

                    impressionProductsGA4.push({
                        item_name: impressionProduct[this.currentIso].title,
                        item_id: impressionProduct.reference,
                        category: impressionProduct[this.currentIso].category_name,
                        item_list_name: 'Main Search',
                        item_list_id: 'main_search',
                        index: x,
                    })
                }

                window.dispatchEvent(new CustomEvent('gtmdata', {
                    detail: {
                        ecommerce: {
                            currencyCode: window.Alpine.store('currency').iso_code,
                            impressions: impressionProducts,
                        },
                        event: 'impression',
                    },
                }))

                window.dispatchEvent(new CustomEvent('gtmdata', {
                    detail: {
                        event: 'view_item_list',
                        ecommerce: {
                            item_list_id: 'main_search',
                            item_list_name: 'Main Search',
                            currency: window.Alpine.store('currency').iso_code,
                            currencyCode: window.Alpine.store('currency').iso_code,
                            items: impressionProductsGA4,
                        },
                    },
                }))

                // choose tab to highlight
                if (this.productHitCount > 0) {
                    this.showingProducts = true
                    this.showingCategories = false
                    this.showingCms = false
                } else if (this.categoryHitCount > 0) {
                    this.showingProducts = false
                    this.showingCategories = true
                    this.showingCms = false
                } else if (this.cmsHitCount > 0) {
                    this.showingProducts = false
                    this.showingCategories = false
                    this.showingCms = true
                }

                this.loading = false
            }
        }).catch((e) => {
            this.loading = false

            this.$dispatch('create-toast', {
                title: 'We encountered a problem...',
                message: e,
                type: 'error',
            })
        })

        return true
    },
    pushQueryToHistory() {
        if (this.searchHistory.length) {
            const existingEntry = this.searchHistory.indexOf(this.query)
            const latestEntry = this.searchHistory[0]

            // Duplicate prevention
            if (existingEntry > -1) {
                this.searchHistory.splice(existingEntry, 1)
                this.searchHistory.unshift(this.query)
                return
            }

            // Accommodate for slow typers and remove last entry if its a start of the current query
            if (latestEntry && this.query.startsWith(latestEntry)) {
                this.searchHistory.splice(0, 1)
            }
        }

        this.searchHistory.unshift(this.query)

        if (this.searchHistory.length > 5) {
            this.searchHistory.pop()
        }

        window.Alpine.store('search').history = this.searchHistory
    },
    clearSearchHistory() {
        this.searchHistory = []
        window.Alpine.store('search').history = this.searchHistory
    },
    redirectToSearchResults(submitUrl) {
        window.location.href = submitUrl + this.urlEncodeSearch
    },
    get hits() {
        if (this.showingCms) {
            return this.cmsHitCount
        }
        return this.categoryHitCount + this.productHitCount
    },
    get urlEncodeSearch() {
        let queryString = `?query=${encodeURIComponent(this.query)}`

        if ((this.categoryHitCount + this.productHitCount) === 0 && this.cmsHitCount > 0) {
            queryString += '&cms=1'
        }

        return queryString
    },
    get urlEncodeSearchFoot() {
        let queryString = `?query=${encodeURIComponent(this.query)}`

        if (this.showingCms === false) {
            queryString += '&cms=0'
        } else if (this.showingCms === true) {
            queryString += '&cms=1'
        }

        return queryString
    },
    get urlHelpHubEncodeSearchFoot() {
        return `?help-query=${encodeURIComponent(this.query)}&page=1`
    },
})

document.addEventListener('alpine:init', () => {
    window.Alpine.data('mainSearch', mainSearch)
})

export default mainSearch
